import type { LearningPlan, PlanChange } from '../../types/learning-plans';

/**
 * What on a learning plan is waiting for administration. The home page counts
 * these and the plans queue filters by them, so both read the same rules.
 */
export type PlanAttention = 'needs_setup' | 'hold_ending' | 'needs_lessons';

const HOLD_WARNING_HOURS = 24;

/** Unpaid and without a held timetable: it needs a teacher and times. */
export const needsSetup = (r: LearningPlan): boolean => r.status === 'requesting' || r.status === 'expired';

/** Held for payment, and the hold lapses within a day. */
export const holdEnding = (r: LearningPlan, now = Date.now()): boolean =>
  r.status === 'held' && Boolean(r.holdUntil) && Date.parse(r.holdUntil ?? '') - now < HOLD_WARNING_HOURS * 3600000;

/** Lessons whose attendance must be settled, or that are owed a replacement. */
export const lessonsNeedingAction = (r: LearningPlan): number =>
  r.lessons.filter((l) => l.status === 'attendance_pending' || l.status === 'owed').length;

export function needsAttention(r: LearningPlan, kind: PlanAttention, now = Date.now()): boolean {
  if (kind === 'needs_setup') return needsSetup(r);
  if (kind === 'hold_ending') return holdEnding(r, now);
  return lessonsNeedingAction(r) > 0;
}

export interface PlanWorkSummary {
  needsSetup: number;
  holdEnding: number;
  lessons: number;
  changes: number;
}

export function summarizePlanWork(requests: LearningPlan[], changes: PlanChange[], now = Date.now()): PlanWorkSummary {
  return {
    needsSetup: requests.filter(needsSetup).length,
    holdEnding: requests.filter((r) => holdEnding(r, now)).length,
    lessons: requests.reduce((sum, r) => sum + lessonsNeedingAction(r), 0),
    changes: changes.filter((c) => c.status === 'pending').length,
  };
}
